import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";

import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IContestPlayerStatsStore} from "data/stores/contest_player_stats/contest_player_stats.store";
import {sumBy} from "lodash";
import {Empty} from "data/types/global";
import {IRosterItem} from "data/types/roster";

export interface IPlayersBarController extends ViewController {
	readonly i18n: ILocalizationStore;

	get contestId(): number;
	get title(): string;
	get players(): IRosterItem["players"][number][];
	get filledPlayersCount(): number;
	get progressPercents(): number;
	get rosterSize(): number;
	get projectedPoints(): Empty<number>;
}

@injectable()
export class PlayersBarController implements IPlayersBarController {
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestPlayerStatsStore)
		public readonly _contestPlayerStatsStore: IContestPlayerStatsStore,
		@inject(Bindings.RosterStore) private readonly _rosterStore: IRosterStore
	) {
		makeAutoObservable(this);
	}

	get contestId() {
		return this._rosterStore.currentContestId;
	}

	get title() {
		const count = this.filledPlayersCount;
		return (
			{
				0: this.i18n.t("players.bar.title_0", "Hit spin to fill your roster"),
				1: this.i18n.t("players.bar.title_1", "Hit spin to draw your pitcher"),
				2: this.i18n.t("players.bar.title_2", "Hit spin to draw your second hitter"),
				3: this.i18n.t("players.bar.title_3", "Hit spin to draw your third hitter"),
				4: this.i18n.t("players.bar.title_4", "Hit spin to draw your final hitter"),
			}[count] || ""
		);
	}

	get filledPlayersCount() {
		return this.filledPlayers.length;
	}

	get filledPlayers() {
		return this.players.filter((it) => !!it.playerId);
	}

	get progressPercents() {
		return (this.filledPlayersCount / this._rosterStore.rosterSize) * 100;
	}

	get rosterSize() {
		return this._rosterStore.rosterSize;
	}

	get players() {
		return this._rosterStore.rosterPlayers;
	}

	get projectedPoints() {
		const players = this.filledPlayers;
		if (!players.length) {
			return;
		}
		return sumBy(players, (rosterPlayer) => {
			const playerStats = this._contestPlayerStatsStore.getPlayerStats(rosterPlayer.playerId);
			return playerStats?.projectedPoints || 0;
		});
	}
}
